import React, { useCallback, useEffect, useState, useContext, useMemo, useRef } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import Button from '../../../../../common/Button/Button';
import { ApiContext } from '../../../../../context/MyContext';
import { Toaster } from '../../../../../common/Toaster/Toaster';
import { useUtils } from '../../../../../context/UtilsContext';
import { formatLocalDateTime } from '../../../../../utils/formatLocalDate';
import {
  fetchCallNotes,
  addCallNote,
  updateCallNote,
  deleteCallNote,
} from '../../../core/api/notesApi';
import SyncStatus from '../../../../../common/SyncStatus';
import {
  userNotesCache,
} from '../../../../../utils/listCache/caches';
import RichNoteEditor from '../../../ui/RichNoteEditor';
import NoteRichContent from '../../../ui/NoteRichContent';
import {
  isEditorContentEmpty,
  sanitizeNoteHtml,
  isLikelyRichHtml,
  plainTextToQuillHtml,
  normalizeNoteContentForDisplay,
  stripPanelNoteMarker,
} from '../../../core/utils/noteRichText';

const formatNoteDateTime = (value) => {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const coachDisplayName = (note) => {
  const first = (note.coach_first_name || '').trim();
  const last = (note.coach_last_name || '').trim();
  const combined = [first, last].filter(Boolean).join(' ');
  if (combined) return combined;
  if (note.employee_name) return note.employee_name;
  if (note.coach_id || note.employee_id) return 'Coach';
  return 'Coach';
};

const coachInitials = (note) => {
  const first = (note.coach_first_name || '').trim();
  const last = (note.coach_last_name || '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  const name = coachDisplayName(note);
  if (name && name !== 'Coach') {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return '?';
};

const noteOwnerId = (note) =>
  note?.coach_id ?? note?.employee_id ?? note?.created_by ?? note?.author_id ?? null;

const parseDurationMinutes = (value) => {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n);
};

const sortNotesByDateDesc = (list) =>
  [...list].sort((a, b) => {
    const ta = new Date(a.call_date || a.date || 0).getTime();
    const tb = new Date(b.call_date || b.date || 0).getTime();
    return tb - ta;
  });

/**
 * @param {Object} props
 * @param {string} props.profileId
 * @param {'full'|'compact'|'panel'} [props.variant='full'] - 'panel' is the
 *   floating client panel's Notes tab (ClientRail, dashboard mode): drops the
 *   call-duration field and swaps a few copy strings for client-preferences
 *   framing, but shares every handler and all other UI with 'full'/'compact'.
 * @param {string|null} [props.cacheKey] - When set, notes for this client are
 *   served from and written to `userNotesCache`, and a "Synced Nm ago" line
 *   with a refresh appears at the top. Cached notes paint immediately and the
 *   fetch revalidates behind them, so reopening a panel never shows a skeleton
 *   over content that is already known. Left null only where there is no
 *   sensible per-client key; every rail passes one.
 */
const NotesTab = ({ profileId, variant = 'full', cacheKey = null }) => {
  const isCompact = variant === 'compact';
  // The floating client panel's Notes tab (ClientRail in dashboard mode):
  // same data/handlers as every other variant, only a few copy strings and
  // the call-duration field differ, since this context is about client
  // preferences rather than call logs.
  const isPanel = variant === 'panel';
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const [callDuration, setCallDuration] = useState('');
  const { empId, employeeData, userId } = useContext(ApiContext);
  const coachId = empId || employeeData?.user_id || userId || null;
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [toastNonce, setToastNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // "Is there already something on screen worth keeping?" — set by a cache
  // paint or a completed fetch. Gates the skeleton, nothing else.
  const hasContentRef = useRef(false);
  const editingIdRef = useRef(null);

  const { sessionUtils } = useUtils();
  const eventTypeId = useMemo(() => {
    const groups = sessionUtils?.event_types;
    if (!Array.isArray(groups)) return undefined;
    for (const group of groups) {
      const list = group?.event_types || (Array.isArray(group) ? group : null);
      if (!Array.isArray(list)) continue;
      const match = list.find((x) => x.description === 'get_user_profile_details');
      if (match?.id) return match.id;
    }
    return undefined;
  }, [sessionUtils]);

  const showToast = useCallback((message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setToastNonce((n) => n + 1);
  }, []);

  const resetDraftState = useCallback(() => {
    setNoteText('');
    setCallDuration('');
    setEditingId(null);
    setEditContent('');
    setEditDuration('');
    editingIdRef.current = null;
  }, []);

  // Drop drafts when switching clients so content is never saved to the wrong profile.
  // With a cacheKey, paint that client's cached notes straight away and let the
  // fetch below revalidate, so reopening a panel isn't a blank loading state.
  useEffect(() => {
    resetDraftState();
    const cached = cacheKey ? userNotesCache.get(cacheKey) : undefined;
    setNotes(cached ? cached.notes : []);
    // Cached notes are already on screen, so the revalidating fetch below must
    // not blank them behind a skeleton. Without this the two effects fight:
    // this one paints from cache and clears loading, and `loadNotes` — running
    // on the very next effect — turns it straight back on, which is why the
    // panel showed a skeleton on every single open despite having the data.
    hasContentRef.current = Boolean(cached);
    setLoading(!cached);
  }, [profileId, cacheKey, resetDraftState]);

  const loadNotes = useCallback(async ({ silent = false } = {}) => {
    if (!eventTypeId || !profileId) {
      setLoading(false);
      return;
    }
    const keepEditor =
      silent || Boolean(editingIdRef.current) || hasContentRef.current;
    if (!keepEditor) setLoading(true);
    try {
      const response = await fetchCallNotes(eventTypeId, profileId);
      if (response.success) {
        const sorted = sortNotesByDateDesc(response.data || []);
        setNotes(sorted);
        hasContentRef.current = true;
        // The whole list is replaced rather than diffed: notes carry no
        // updated_at, and call_date is coach-entered so an edit doesn't move
        // it — there is nothing reliable to compare.
        if (cacheKey) userNotesCache.set(cacheKey, { notes: sorted });
      } else {
        showToast(response.message || 'Could not load notes.', 'error');
      }
    } catch {
      showToast('Could not load notes.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventTypeId, profileId, showToast, cacheKey]);

  // Runs on mount and whenever the client changes — this is the background
  // sync that picks up notes added on another screen.
  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Forced refresh: drop this client's entry so nothing stale can be replayed,
  // then refetch without blanking the list behind a loading state.
  const handleRefresh = useCallback(() => {
    if (cacheKey) userNotesCache.remove(cacheKey);
    setRefreshing(true);
    loadNotes({ silent: true });
  }, [cacheKey, loadNotes]);

  const isOwnNote = (note) =>
    coachId != null && String(noteOwnerId(note)) === String(coachId);

  const noteCount = notes.length;
  const sessionReady = Boolean(eventTypeId) && Boolean(profileId) && Boolean(coachId);

  const canAdd =
    sessionReady &&
    !isEditorContentEmpty(noteText);

  const sendNote = async () => {
    if (!eventTypeId || !coachId) {
      showToast('Coach session not ready. Refresh and try again.', 'error');
      return;
    }
    if (isEditorContentEmpty(noteText)) {
      showToast('Write something before adding a note.', 'error');
      return;
    }

    setSaving(true);
    try {
      const now = new Date();
      const currentTime = formatLocalDateTime(now);
      const durationVal = parseDurationMinutes(callDuration);

      const postResponse = await addCallNote(eventTypeId, {
        client_id: profileId,
        coach_id: coachId,
        content: sanitizeNoteHtml(noteText),
        duration: durationVal,
        call_date: currentTime,
      });

      if (postResponse.success) {
        showToast('Note added');
        setNoteText('');
        setCallDuration('');
        await loadNotes({ silent: true });
      } else {
        showToast(postResponse.message || 'Could not save the note.', 'error');
      }
    } catch {
      showToast('Could not save the note.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    const id = note.id;
    setEditingId(id);
    editingIdRef.current = id;
    const raw = stripPanelNoteMarker(note.content || '');
    const normalized = normalizeNoteContentForDisplay(raw);
    setEditContent(
      isLikelyRichHtml(normalized) ? normalized : plainTextToQuillHtml(normalized)
    );
    setEditDuration(
      note.duration != null && note.duration !== ''
        ? String(note.duration)
        : note.call_duration != null
          ? String(note.call_duration)
          : ''
    );
  };

  const cancelEdit = () => {
    setEditingId(null);
    editingIdRef.current = null;
    setEditContent('');
    setEditDuration('');
  };

  const saveEdit = async (note) => {
    if (!eventTypeId || !coachId) {
      showToast('Coach session not ready. Refresh and try again.', 'error');
      return;
    }
    if (editingId == null) {
      showToast('No note selected to update.', 'error');
      return;
    }
    if (isEditorContentEmpty(editContent)) {
      showToast('Note cannot be empty.', 'error');
      return;
    }

    setEditSaving(true);
    try {
      const durationVal = parseDurationMinutes(editDuration);
      const callDate =
        note.call_date || note.date || formatLocalDateTime(new Date());

      const res = await updateCallNote(eventTypeId, {
        id: note.id,
        client_id: profileId,
        coach_id: coachId,
        content: sanitizeNoteHtml(editContent),
        duration: durationVal,
        call_date: callDate,
      });

      if (res.success) {
        showToast('Note updated');
        cancelEdit();
        await loadNotes({ silent: true });
      } else {
        showToast(res.message || 'Could not update the note.', 'error');
      }
    } catch {
      showToast('Could not update the note.', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const removeNote = async (noteId) => {
    if (!eventTypeId) {
      showToast('Coach session not ready. Refresh and try again.', 'error');
      return;
    }
    if (
      !window.confirm(
        'Delete this note? This cannot be undone.'
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const res = await deleteCallNote(eventTypeId, noteId);
      if (res.success) {
        showToast('Note deleted');
        if (String(editingId) === String(noteId)) cancelEdit();
        await loadNotes({ silent: true });
      } else {
        showToast(res.message || 'Could not delete the note.', 'error');
      }
    } catch {
      showToast('Could not delete the note.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const emptyState = useMemo(
    () => (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 px-6 py-12 text-center">
        <p className="text-lg font-medium text-gray-800">No notes yet</p>
        <p className="mt-2 max-w-md mx-auto text-sm text-gray-600">
          {isPanel
            ? 'Write down what this client should avoid, what they prefer, and anything the next coach needs to know.'
            : 'When anyone on the coaching team adds a note, it will show up here with their name and time. Use notes for call summaries, follow-ups, and context the whole team should see.'}
        </p>
      </div>
    ),
    [isPanel]
  );

  return (
    <div className={`w-full mx-auto px-1 ${isCompact ? 'max-w-full' : 'max-w-4xl'}`}>
      {alertMessage ? (
        <Toaster key={toastNonce} message={alertMessage} type={alertType} />
      ) : null}

      {/* Only where the call site opted into caching — elsewhere the notes are
          always freshly fetched and a "synced" line would be noise. */}
      {cacheKey && (
        <div className="mb-3 flex items-center justify-end">
          <SyncStatus
            syncedAt={userNotesCache.peek(cacheKey)?.ts ?? null}
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
        </div>
      )}

      {!isPanel && (
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Team notes
          </h1>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Shared call notes for this member. Every coach can add notes; you can
            edit or delete only the notes you wrote.
          </p>
          {!loading && (
            <p className="mt-2 text-xs text-gray-500">
              {noteCount === 0
                ? 'No entries yet'
                : `${noteCount} ${noteCount === 1 ? 'entry' : 'entries'}`}
            </p>
          )}
          {!sessionReady && !loading ? (
            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
              Session is still loading. Refresh if Add note stays disabled.
            </p>
          ) : null}
        </div>
      )}

      <section
        className={
          isPanel
            ? 'mb-6'
            : 'rounded-xl border border-gray-200 bg-white p-5 shadow-sm mb-8'
        }
        aria-labelledby="add-note-heading"
      >
        {isPanel ? (
          <div className="flex items-baseline gap-2 flex-wrap">
            <h2 id="add-note-heading" className="text-sm font-semibold text-gray-900">
              Add a note
            </h2>
            <p className="text-xs text-gray-500">
              Record this client's preferences, restrictions, and care notes —
              visible to every coach on their account.
            </p>
          </div>
        ) : (
          <>
            <h2 id="add-note-heading" className="text-sm font-semibold text-gray-900">
              Add a note
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Log a call or session update. Use the toolbar for bold, lists, and
              quotes. Visible to all coaches on this account.
            </p>
          </>
        )}
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/80 overflow-hidden focus-within:ring-2 focus-within:ring-orange-primary/20 focus-within:border-orange-primary">
          <RichNoteEditor
            id="new-note-body"
            value={noteText}
            onChange={setNoteText}
            placeholder={
              isPanel
                ? 'e.g. Prefers morning sessions, avoids dairy, has a mild knee issue…'
                : 'e.g. Discussed protein targets, agreed to check in next week…'
            }
          />
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          {isPanel ? (
            <p className="text-xs text-gray-400">
              Only you can edit or delete your own notes.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="new-call-duration"
                className="text-xs font-medium text-gray-600"
              >
                Call duration (optional)
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="new-call-duration"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={callDuration}
                  onChange={(e) => setCallDuration(e.target.value)}
                  placeholder="0"
                  className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm focus:border-orange-primary focus:outline-none focus:ring-2 focus:ring-orange-primary/20"
                />
                <span className="text-xs text-gray-500">minutes</span>
              </div>
            </div>
          )}
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <Button
              type="button"
              text={saving ? 'Saving…' : 'Add note'}
              onClick={sendNote}
              disabled={!canAdd || saving}
              className="bg-orange-primary text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed min-w-[7rem]"
            />
            {!canAdd && !saving ? (
              <p className="text-[11px] text-gray-500 max-w-[16rem] sm:text-right">
                {!sessionReady
                  ? 'Session still loading — refresh if Add stays disabled.'
                  : 'Write a note above to enable Add.'}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <section aria-labelledby="timeline-heading">
        <div className="flex items-baseline justify-between gap-2 mb-4">
          <h2 id="timeline-heading" className="text-sm font-semibold text-gray-900">
            Timeline
          </h2>
          <span className="text-xs text-gray-500">Newest first</span>
        </div>

        <div className="relative">
        <div className="max-h-[min(420px,50vh)] overflow-y-auto pr-1">
        {loading ? (
          <div className="space-y-4" aria-busy="true" aria-label="Loading notes">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-gray-100 bg-white p-4 h-28"
              />
            ))}
          </div>
        ) : notes.length === 0 ? (
          emptyState
        ) : (
          <ul className="space-y-4 list-none p-0 m-0">
            {notes.map((note) => {
              const own = isOwnNote(note);
              const name = coachDisplayName(note);
              const isEditing = String(editingId) === String(note.id);

              return (
                <li key={note.id}>
                  <article className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-primary/90" />
                    <div className="flex gap-4 p-4 pl-5 pr-3">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-orange-primary/15 text-sm font-semibold text-orange-primary"
                        aria-hidden
                      >
                        {coachInitials(note)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                          <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-medium text-gray-900">{name}</span>
                            {own ? (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                You
                              </span>
                            ) : null}
                            <span className="text-xs text-gray-500">
                              {formatNoteDateTime(note.call_date || note.date)}
                            </span>
                          </div>
                          {own && !isEditing ? (
                            <div className="flex flex-shrink-0 items-center justify-end gap-2 sm:pt-0">
                              <button
                                type="button"
                                onClick={() => startEdit(note)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:border-orange-200 hover:bg-orange-50/80 hover:text-orange-primary focus:outline-none focus:ring-2 focus:ring-orange-primary/30"
                              >
                                <Pencil className="h-3.5 w-3.5" aria-hidden />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => removeNote(note.id)}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 shadow-sm transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:opacity-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                Delete
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {(note.duration != null && note.duration !== '') ||
                          (note.call_duration != null && note.call_duration !== '')
                            ? `${note.duration ?? note.call_duration} min call`
                            : 'No duration logged'}
                        </p>

                        {isEditing ? (
                          <div className="mt-3 space-y-3">
                            <div
                              key={String(note.id)}
                              className="rounded-lg border border-gray-200 bg-gray-50/80 overflow-hidden focus-within:ring-2 focus-within:ring-orange-primary/20 focus-within:border-orange-primary"
                            >
                              <RichNoteEditor
                                id={`edit-note-${note.id}`}
                                value={editContent}
                                onChange={setEditContent}
                                placeholder="Edit your note…"
                              />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <label
                                className="text-xs text-gray-600"
                                htmlFor={`edit-duration-${note.id}`}
                              >
                                Duration (min)
                              </label>
                              <input
                                id={`edit-duration-${note.id}`}
                                type="number"
                                min={0}
                                step={1}
                                value={editDuration}
                                onChange={(e) => setEditDuration(e.target.value)}
                                className="w-20 rounded border border-gray-200 px-2 py-1 text-sm"
                              />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => saveEdit(note)}
                                disabled={editSaving}
                                className="rounded-lg bg-orange-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                              >
                                {editSaving ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={editSaving}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <NoteRichContent content={stripPanelNoteMarker(note.content)} />
                        )}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
        </div>
        {/* Cheap overflow hint: shown whenever there are enough cards to likely
            exceed the box, not tied to actual scroll position — good enough to
            signal "there's more" without tracking scroll state. */}
        {!loading && notes.length > 2 && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 rounded-b-xl bg-gradient-to-t from-gray-300/70 to-transparent"
          />
        )}
        </div>
      </section>
    </div>
  );
};

export default NotesTab;
